/*
  File: AuthProvider.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the AuthProvider component, which manages authentication state including
  user login, logout, and token decoding. It also provides authentication context for child components.
*/

import React, { ReactNode, useContext, useEffect, useState } from "react";
import { AgentInstance, AuthContextType, User } from "../types";
import { jwtDecode } from "jwt-decode";
import { getAgent, getAgentByEmail, getUser } from "../service/service";

const AuthContext = React.createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  agentInstance: null,
  setToken: () => {},
  setAgent: () => {},
  logout: () => {},
});

export default AuthContext;

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [agentInstance, setAgentInstance] = useState<AgentInstance | null>(
    null
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setToken(token);
    }
  }, []);

  const setToken = async (token: string) => {
    try {
      const decodedToken: any = jwtDecode(token);
      if (decodedToken) {
        const user_id = decodedToken.user_id;
        const user: User = await getUser(user_id);
        setUser(user);
        await setAgent(user.email);
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
        throw new Error("Invalid token");
      }
    } catch (error) {
      setIsLoggedIn(false);
      throw error;
    }
  };

  const setAgent = async (emailAddress: string) => {
    try {
      const agentInstance: AgentInstance = await getAgentByEmail(emailAddress);
      setAgentInstance(agentInstance);
    } catch (error) {
      setAgentInstance(null);
      return null;
    }
  };

  const logout = () => {
    const token = localStorage.getItem("token");
    if (token) {
      localStorage.removeItem("token");
    }
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: isLoggedIn,
        user: user,
        agentInstance: agentInstance,
        setAgent: setAgent,
        setToken: setToken,
        logout: logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
